/**
 * railIcons — the HubRail icon set (CD-12 / 4A).
 *
 * A faithful port of the Claude Design prototype `nav-rail/icons.jsx`. Each
 * glyph is a 24×24 stroke path, subpaths '|'-joined, rendered by `RailIcon` at
 * ~1.7 stroke with round joins. THE RULE: one unique glyph per concept — no
 * glyph is shared between two different destinations (the app's prior rail
 * reused several). Deep-surface pins reuse their *kind* glyph (a pinned
 * pipeline = the Pipelines funnel) — the same concept, not a reuse.
 *
 * These are pure path data (no color) — the rail's color comes from the
 * `--rail-*` theme tokens, so this file adds zero raw hex.
 */
import React from 'react';

// circle helper — two arcs make a full circle centered (cx,cy) radius r
const c = (cx: number, cy: number, r: number): string =>
  `M${cx} ${cy - r}a${r} ${r} 0 100 ${r * 2}a${r} ${r} 0 100 ${-r * 2}`;

export type RailIconKey = keyof typeof RIC;

export const RIC = {
  /* ── chrome / affordances ── */
  grid: 'M3 3h7v7H3z|M14 3h7v7h-7z|M3 14h7v7H3z|M14 14h7v7h-7z',
  collapse: 'M13 6l-5 6 5 6|M19 6l-5 6 5 6',
  expand: 'M11 6l5 6-5 6|M5 6l5 6-5 6',
  chevRight: 'M9 6l6 6-6 6',
  chevDown: 'M6 9l6 6 6-6',
  chevUp: 'M18 15l-6-6-6 6',
  search: 'M20.5 20.5l-4.2-4.2|M11 18a7 7 0 100-14 7 7 0 000 14z',
  star: 'M12 3.2l2.6 5.5 6 .8-4.4 4.2 1.1 6L12 17.1 6.1 19.7l1.1-6-4.4-4.2 6-.8z',
  grip: 'M8 6h.01M8 12h.01M8 18h.01M14 6h.01M14 12h.01M14 18h.01',
  pin: 'M12 15v6|M8 3h8l-1 7 2.4 2.4H6.6L9 10z',
  diamond: 'M12 3l9 9-9 9-9-9z',
  close: 'M18 6L6 18|M6 6l12 12',
  drill: 'M8 4.5l7 7.5-7 7.5',
  bell: 'M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9|M13.7 21a2 2 0 01-3.4 0',
  eye: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z|' + c(12, 12, 3),
  sparkle: 'M12 3l1.8 5.4L19 10l-5.2 1.6L12 17l-1.8-5.4L5 10l5.2-1.6z',

  /* ── section leads (also the collapsed-rail group markers) — each unmistakable ── */
  secPinned: 'M12 15v6|M8 3h8l-1 7 2.4 2.4H6.6L9 10z', // pin
  secGeneral: 'M3 11l9-8 9 8|M5 9.4V20h5v-6h4v6h5V9.4', // home base
  secOps: 'M4 7.2l8-4 8 4-8 4z|M4 12l8 4 8-4|M4 16.4l8 4 8-4', // stacked layers
  secSales: c(12, 12, 9) + '|' + c(12, 12, 4.4) + '|M12 12l7-7|M14.6 5h4.4v4.4', // bullseye + dart
  secMarketing:
    c(12, 12, 2) +
    '|M8.8 8.8a4.5 4.5 0 000 6.4|M15.2 8.8a4.5 4.5 0 010 6.4|M6.3 6.3a8 8 0 000 11.4|M17.7 6.3a8 8 0 010 11.4', // broadcast
  secTools: 'M14.6 6.4a4 4 0 00-5.4 5.4L3 18l3 3 6.2-6.2a4 4 0 005.4-5.4l-2.6 2.6-2.6-2.6z', // wrench
  secAnalytics: 'M4 20h16|M6.5 20v-5|M11 20V8.5|M15.5 20v-9.5', // bar chart (dept)
  secAI: 'M12 3l1.8 5.4L19 10l-5.2 1.6L12 17l-1.8-5.4L5 10l5.2-1.6z', // sparkle
  secEOS: 'M12 3l7.8 4.5v9L12 21l-7.8-4.5v-9z|M12 8.4l3.3 1.9v3.4L12 15.6l-3.3-1.9v-3.4z', // nested hex
  secAdmin:
    'M4 7h4|M12 7h8|M4 12h10|M18 12h2|M4 17h6|M14 17h6|' +
    c(10, 7, 2) + '|' + c(16, 12, 2) + '|' + c(12, 17, 2), // control sliders

  /* ── GENERAL ── */
  console: 'M3 3h8v9H3z|M13 3h8v5h-8z|M13 12h8v9h-8z|M3 16h8v5H3z',
  mail: 'M3 5h18v14H3z|M3 6l9 6.5L21 6',
  calendar: 'M3 4h18v18H3z|M3 10h18|M8 2v4|M16 2v4',
  todos: 'M10 6h11|M10 12h11|M10 18h11|M3 6.4l1.4 1.4L7 5.4|M3 12.4l1.4 1.4L7 11.4|M3 18.4l1.4 1.4L7 17.4',
  /* the Systems Assessment prep (Phase 2c): a clipboard with a tick (canvas ta-kit `prep`) */
  prep: 'M9 4h6v3H9z|M7 5.5H5V21h14V5.5h-2|M8.5 12l2 2 4-4.5|M8.5 17h7',
  herd: 'M7.6 17a4 4 0 01-.8-7.95A5 5 0 0116.4 7.6 3.7 3.7 0 0116.2 17z|M8.7 13h.01|M12 13h.01|M15.3 13h.01', // thought cloud
  tracker: c(12, 13.6, 7.4) + '|M9 2.6h6|M12 2.6V5.4|M19 6.4l-1.5 1.5|M10.6 10.8l4.1 2.8-4.1 2.8z', // stopwatch + play
  contacts: 'M6 3h12a1 1 0 011 1v16a1 1 0 01-1 1H6a1 1 0 01-1-1V4a1 1 0 011-1z|M5 8H3|M5 12H3|M5 16H3|' + c(12, 9, 2) + '|M9 16a3 3 0 016 0',
  templates: 'M4 4h16v5H4z|M4 12h7v8H4z|M14 12h6v8h-6z',

  /* ── OPERATIONS ── */
  helpdesk: 'M4 13a8 8 0 0116 0|M2 14a2 2 0 012-2h1v6H4a2 2 0 01-2-2z|M22 14a2 2 0 00-2-2h-1v6h1a2 2 0 002-2z|M18 18v.5a3 3 0 01-3 3h-2',
  projects: 'M4 4h16v16H4z|M9.3 4v16|M14.6 4v16|M6 7.4h1.4M11.3 7.4h1.4M16.6 7.4h1.4', // kanban board
  ticket: 'M3 8a2 2 0 012-2h14a2 2 0 012 2v2a2 2 0 000 4v2a2 2 0 01-2 2H5a2 2 0 01-2-2v-2a2 2 0 000-4z|M13 6v12',
  clients: 'M4 21V5a1 1 0 011-1h8a1 1 0 011 1v16|M14 21V9h5a1 1 0 011 1v11|M3 21h18|M7 8h3M7 12h3M7 16h3',
  portal: 'M5 21V11a7 7 0 0114 0v10|M3 21h18|' + c(12, 14.6, 1.3) + '|M12 15.9v2', // archway + keyhole — a secured client portal

  /* ── SALES & MARKETING ── */
  pipeline: 'M3 5h18l-7 8v5l-4 2v-7z',
  campaigns: 'M4 10l11-5v14L4 14z|M15 8.2a3.4 3.4 0 010 5.6|M7 15v2.4a2 2 0 004 0',
  affiliates: c(6, 12, 2.4) + '|' + c(17, 6.5, 2.4) + '|' + c(17, 17.5, 2.4) + '|M8.3 10.9l6.5-3.5|M8.3 13.1l6.5 3.5',
  content: 'M4 5h16v14H4z|M4 16l4.5-4.5 3 3L18 7l2 2|' + c(9, 9.5, 1.5),
  newsletters: 'M22 4L11 13|M22 4l-7 17-4-7-7-4z',
  contracts: 'M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z|M14 3v5h5|M8 17c1.2-1.8 2.8-1.8 4 0s2.8 1.8 4 0',
  scraper: 'M12 3v9|M8.5 8.5L12 12l3.5-3.5|M4 15v3a2 2 0 002 2h12a2 2 0 002-2v-3',

  /* ── TOOLS ── */
  docs: 'M12 6.4C10.5 5 8 4.5 5 5v13c3-.5 5.5 0 7 1.5M12 6.4C13.5 5 16 4.5 19 5v13c-3-.5-5.5 0-7 1.5M12 6.4V20', // open book
  comms: 'M21 15a2 2 0 01-2 2H8l-4 4V5a2 2 0 012-2h13a2 2 0 012 2z|M8 9h9M8 12.5h6',
  forms: 'M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z|M14 3v5h5|M8 9h2|M8 13h6M8 17h4',
  notes: 'M9 4h9a1 1 0 011 1v14a1 1 0 01-1 1H9zM9 4v16|M6 6.5h3M6 10h3M6 13.5h3M6 17h3|M12 9h4M12 12.5h3', // spiral notebook
  scheduler: 'M4 4h16v6H4z|M4 4h16M8 2v4M16 2v4|' + c(16.5, 17.5, 3.5) + '|M16.5 16v1.5l1.3 1',

  /* ── AI ── */
  mission: c(12, 12, 9) + '|M15.6 8.4l-2.2 5.4-5.4 2.2 2.2-5.4z', // compass rose
  ledger: 'M6 3h9l3 3v15H6a1 1 0 01-1-1V4a1 1 0 011-1z|M15 3v3h3|M8 9h7M8 12h7M8 15h4|M16.7 16.4l.8 1.7 1.7.8-1.7.8-.8 1.7-.8-1.7-1.7-.8 1.7-.8z',

  /* ── ANALYTICS ── */
  scorecard: 'M4 5h16v14H4z|M4 12h16|M12 5v14|M6.4 9.6l2 2 3.1-3.6', // KPI grid + rising tick
  forecast: 'M3 16l5-5 3.5 3L20 6|M16 6h4v4|M3 20h18',
  reports: 'M6 3h9l4 4v13a1 1 0 01-1 1H6a1 1 0 01-1-1V4a1 1 0 011-1z|M15 3v4h4|' + c(11, 14, 3) + '|M11 12.5V14l1.2 .9',
  finance: 'M12 2v20|M17 6H9.5a3 3 0 000 6h4a3 3 0 010 6H7',
  invoice: 'M6 3h12v18l-2.6-1.6L13 21l-2.4-1.6L8 21l-2-1.6V3z|M9 8h6M9 12h6M9 16h3', // receipt / invoice

  /* ── EOS ── */
  eosScore: 'M4 5h16v14H4z|M4 9.5h16|M4 14h16|M9 5v14|M6 7.2h1.6|M6 11.7h1.6|M6 16.2h1.6', // measurables table
  rocks: 'M12 2l8 7-3 11H7L4 9z|M9 9l3 3 3-3|M4 9h16',
  vto: 'M6 3h8l4 4v13a1 1 0 01-1 1H6a1 1 0 01-1-1V4a1 1 0 011-1z|M14 3v4h4|' + c(12, 14.5, 2.2) + '|M7.6 14.5c1.6-2.5 7.2-2.5 8.8 0-1.6 2.5-7.2 2.5-8.8 0z',
  ids: c(12, 12, 9) + '|M8 12l3 3 5-6',
  accountability: 'M9 3h6v4H9z|M4 17h6v4H4z|M14 17h6v4h-6z|M12 7v4|M7 17v-2h10v2',
  l10ops: 'M8 4h8v3H8z|M6 6h12v13a1 1 0 01-1 1H7a1 1 0 01-1-1z|M15 12a3 3 0 10-.7 3.2|M15 11v2h-2',
  l10sm: 'M4 4h16v11H4z|M8 11l2.6-3 2 1.6L16 6|M12 15v4|M8 21l4-2 4 2',
  kickoff: 'M6 21V4|M6 4h11l-2.2 3.2L17 10.5H6',
  leadership: c(8, 7, 2.2) + '|' + c(16, 7, 2.2) + '|M4 18a4 4 0 018 0|M12 18a4 4 0 018 0|M12 2.5l.6 1.3 1.4.1-1.1 1 .3 1.4-1.2-.7-1.2.7.3-1.4-1.1-1 1.4-.1z',
  eosQuarterly: 'M4 5h16v15H4z|M4 9h16|M8 3v4M16 3v4|M9.5 15.2a3 3 0 105-2.3|M14.7 10.9v2.2h-2.2', // calendar + recur
  eosReviews: c(8, 8, 2.3) + '|' + c(16, 8, 2.3) + '|M3.5 19a4.5 4.5 0 019 0|M11.5 19a4.5 4.5 0 019 0',
  gwc: 'M4 5l2 2 3-3|M13 5h7|M4 12l2 2 3-3|M13 12h7|M4 19l2 2 3-3|M13 19h7',
  eosAssess: 'M4 15a8 8 0 0116 0|M12 15l4-4|M6 18h.01M18 18h.01',

  /* ── ADMIN ── */
  sandbox: 'M9 3h6M10 3.5v4.5L5.6 17a2 2 0 001.8 3h9.2a2 2 0 001.8-3L14 8V3.5|M8 14h8',
  canvas: 'M4 4h16v16H4z|M4 9h16M9 9v11|M6.5 6.5h.01',
  engineering: 'M8 5l-5 7 5 7M16 5l5 7-5 7|M13 4l-2 16',
  aihr: c(12, 8, 3.4) + '|M5 21a7 7 0 0114 0|M18 4l.7 1.5L20 6l-1.3.5L18 8l-.7-1.5L16 6l1.3-.5z',
  smoketests: c(12, 12, 8) + '|M12 8v4l2.8 1.6|M12 4V2m0 20v-2',

  /* ── footer / chrome ── */
  whatsnew: 'M12 3v3.5|M12 17.5V21|M3.5 12H7|M17 12h3.5|M6 6l2.4 2.4|M17.6 6l-2.4 2.4|M6 18l2.4-2.4|M17.6 18l-2.4-2.4|' + c(12, 12, 2.6),
  feedback: 'M5 4h14v10H8l-4 4V4z|M8.5 8.5h7|M8.5 11h4',
  // Dev Mode. A beetle: capsule body with a centre seam, antennae, three legs a
  // side. Its own glyph rather than a borrowed sparkle/eye — the rail's rule is
  // one unique mark per concept, and this one has to be unmistakable at 16px
  // because it arms diagnostic capture.
  bug: 'M9 5.5l-1.5-2|M15 5.5l1.5-2|M8 8.5a4 4 0 018 0v4a4 4 0 01-8 0z|M12 8.5v8|M8 10H4.5|M8 13.5H5|M8 17l-3 2|M16 10h3.5|M16 13.5h3|M16 17l3 2',
  settings:
    c(12, 12, 3) +
    '|M19.4 15a1.6 1.6 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.6 1.6 0 00-2.7 1.1V21a2 2 0 01-4 0v-.1A1.6 1.6 0 007 19.4a1.6 1.6 0 00-1.8.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.6 1.6 0 00-1.1-2.7H1a2 2 0 010-4h.1A1.6 1.6 0 002.6 7a1.6 1.6 0 00-.3-1.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.6 1.6 0 001.8.3H7a1.6 1.6 0 001-1.5V1a2 2 0 014 0v.1a1.6 1.6 0 001 1.5 1.6 1.6 0 001.8-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.6 1.6 0 00-.3 1.8V7a1.6 1.6 0 001.5 1H23a2 2 0 010 4h-.1a1.6 1.6 0 00-1.5 1z',
  logout: 'M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4|M16 17l5-5-5-5|M21 12H9',

  /* ── the client portal's own concepts (free-app round 1) ──
   * Ported verbatim from the design packet's `portal-free-app/icons.jsx`.
   * Three glyphs only: the design reuses `docs` for Workflows and `secEOS`
   * for EOS, so those two concepts are NOT duplicated here. They live in the
   * rail's set rather than a portal-local one because the portal renders the
   * SAME HubRail — a second icon registry is how two rails start to drift. */
  roadmap: 'M4 19c1-5 4-6 8-6s7-1 8-6|' + c(4, 19, 2) + '|' + c(12, 13, 2) + '|' + c(20, 7, 2), // route through three nodes
  sops: 'M6 3h9l4 4v14H6z|M15 3v4h4|M9 12.5l2 2 4-4.5|M9 17.5h6', // page with a checked rule
  injector: 'M13 4h7v16h-7|M3 12h11|M10 8l4 4-4 4', // arrow entering a box
} as const;

export function RailIcon({
  d,
  size = 18,
  color = 'currentColor',
  sw = 1.7,
  style,
}: {
  d: string;
  size?: number;
  color?: string;
  sw?: number;
  style?: React.CSSProperties;
}): React.ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      style={style}
      stroke={color}
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {String(d)
        .split('|')
        .map((p, i) => (
          <path key={i} d={p} />
        ))}
    </svg>
  );
}
